import { useEffect, useState } from "react";
import { BookOpen, Code2, Sparkles } from "lucide-react";
import { useMotion } from "./motion";


const renderInline = (text) =>
    text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**"))
            return (
                <strong key={i} className="font-semibold text-slate-100">
                    {p.slice(2, -2)}
                </strong>
            );
        if (p.startsWith("`") && p.endsWith("`"))
            return (
                <code
                    key={i}
                    className="rounded bg-slate-950/80 px-1 py-0.5 font-mono text-[10px] text-cyan-300"
                >
                    {p.slice(1, -1)}
                </code>
            );
        return p;
    });

const renderContent = (text) => {
    const parts = text.split(/```(\w+)?\n?([\s\S]*?)(?:```|$)/g);
    const out = [];
    for (let i = 0; i < parts.length; i++) {
        if (i % 3 === 0) {
            if (parts[i]?.trim())
                out.push(
                    <p key={i} className="whitespace-pre-wrap leading-relaxed">
                        {renderInline(parts[i])}
                    </p>,
                );
        } else if (i % 3 === 2) {
            out.push(
                <pre
                    key={i}
                    className="overflow-x-auto rounded-md border border-slate-800 bg-slate-950/80 p-3 font-mono text-[11px] leading-relaxed text-cyan-100"
                >
                    {parts[i]}
                </pre>,
            );
        }
    }
    return out;
};

// scripted demo conversations — the mock assistant plays these on a loop
const SCRIPTS = {
    docs: [
        {
            q: "How do I verify a webhook signature?",
            a: "Every delivery carries `X-MDH-Signature: t=<ts>,v1=<hmac>`. Compute **HMAC-SHA256** of `t.rawBody` with your endpoint secret and compare it to `v1` — match means it's genuinely from MDH.",
        },
        {
            q: "What happens when my endpoint is down?",
            a: "Deliveries retry automatically at **1s, 5s and 30s**. If the last attempt fails, the delivery is marked failed and you can replay it any time from the dashboard or the API.",
        },
        {
            q: "Can I get alerted when a monitor flips?",
            a: "Yes — REST, cron and SSL monitors emit **down / recovered** events, and with SMS opt-in you get a text the moment an endpoint goes down (and when it comes back).",
        },
    ],
    code: [
        {
            q: "Node handler that verifies signatures",
            a: '```js\nconst crypto = require("crypto");\n\nfunction verifyMdh(raw, header, secret) {\n  const [t, v1] = header.split(",").map(s => s.split("=")[1]);\n  const mac = crypto.createHmac("sha256", secret)\n    .update(`${t}.${raw}`).digest("hex");\n  return crypto.timingSafeEqual(Buffer.from(mac), Buffer.from(v1));\n}\n```',
        },
        {
            q: "Publish an event with cURL",
            a: '```bash\ncurl -X POST https://mdhapi.com/api/ingest/app_…/events \\\n  -H "Authorization: Bearer mdh_pat_…" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"type":"payment.ok","data":{"id":"inv_42"}}\'\n```',
        },
    ],
};

export default function AiAssistant({ fill = false }) {
    const [mode, setMode] = useState("docs");
    const [pairIdx, setPairIdx] = useState(0);
    const [qLen, setQLen] = useState(0);
    const [aLen, setALen] = useState(0);
    const [phase, setPhase] = useState("q"); // q → think → a → hold → fade
    const { reduced } = useMotion();

    const script = SCRIPTS[mode];
    const pair = script[pairIdx % script.length];

    // restart the loop when the mode tab changes
    useEffect(() => {
        setPairIdx(0);
        setQLen(0);
        setALen(0);
        setPhase("q");
    }, [mode]);

    // typewriter loop (respects reduced-motion: jumps straight to full text)
    useEffect(() => {
        if (reduced) {
            setQLen(pair.q.length);
            setALen(pair.a.length);
            setPhase("hold");
            return undefined;
        }
        let t;
        if (phase === "q") {
            if (qLen < pair.q.length) t = setTimeout(() => setQLen((n) => n + 1), 36);
            else setPhase("think");
        } else if (phase === "think") {
            t = setTimeout(() => setPhase("a"), 800);
        } else if (phase === "a") {
            if (aLen < pair.a.length) t = setTimeout(() => setALen((n) => Math.min(n + 4, pair.a.length)), 13);
            else t = setTimeout(() => setPhase("hold"), 300);
        } else if (phase === "hold") {
            t = setTimeout(() => setPhase("fade"), 3400);
        } else if (phase === "fade") {
            t = setTimeout(() => {
                setPairIdx((i) => i + 1);
                setQLen(0);
                setALen(0);
                setPhase("q");
            }, 550);
        }
        return () => clearTimeout(t);
    }, [phase, qLen, aLen, pair, reduced]);

    const typingQ = phase === "q";
    const thinking = phase === "think";

    return (
        <div
            data-testid="ai-assistant"
            className={`glass-panel overflow-hidden rounded-xl shadow-[0_30px_60px_-30px_rgba(0,0,0,0.8)] ${
                fill ? "flex h-full flex-col" : ""
            }`}
        >
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-800/80 px-4 py-3">
                <div className="flex items-center gap-1 rounded-md border border-slate-800 bg-slate-950/60 p-0.5">
                    {[
                        { id: "docs", label: "Ask the docs", Icon: BookOpen },
                        { id: "code", label: "Generate code", Icon: Code2 },
                    ].map(({ id, label, Icon }) => (
                        <button
                            key={id}
                            data-testid={`assistant-mode-${id}`}
                            onClick={() => setMode(id)}
                            className={`flex items-center gap-1.5 rounded px-3 py-1.5 font-mono text-[11px] transition-colors ${
                                mode === id
                                    ? "bg-cyan-950/70 text-cyan-300"
                                    : "text-slate-500 hover:text-slate-300"
                            }`}
                        >
                            <Icon className="h-3 w-3" />
                            {label}
                        </button>
                    ))}
                </div>
                <span
                    data-testid="assistant-demo-badge"
                    className="rounded-full border border-amber-500/40 bg-amber-950/40 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-300"
                >
                    demo preview
                </span>
            </div>

            <div
                data-testid="assistant-messages"
                className={`space-y-3 overflow-y-auto p-4 transition-opacity duration-500 ${
                    phase === "fade" ? "opacity-0" : "opacity-100"
                } ${fill ? "min-h-0 flex-1" : "h-[380px]"}`}
            >
                <div className="ml-auto max-w-[85%] rounded-lg border border-cyan-500/25 bg-cyan-950/40 px-3.5 py-2.5 text-xs text-cyan-50">
                    <p className="whitespace-pre-wrap leading-relaxed">
                        {pair.q.slice(0, qLen)}
                        {typingQ && <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-cyan-300 align-middle" />}
                    </p>
                </div>
                <div
                    data-testid="assistant-message-assistant"
                    className="mr-auto max-w-[85%] space-y-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3.5 py-2.5 text-xs text-slate-300"
                >
                    {thinking || aLen === 0 ? (
                        <span className="inline-flex items-center gap-2 font-mono text-[11px] text-slate-500">
                            <span className="h-3 w-1.5 animate-pulse bg-cyan-400" />
                            thinking…
                        </span>
                    ) : (
                        <>
                            {renderContent(pair.a.slice(0, aLen))}
                            {phase === "a" && (
                                <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-cyan-400 align-middle" />
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="shrink-0 space-y-2.5 border-t border-slate-800/80 p-4">
                <p className="flex items-start gap-2 font-mono text-[11px] leading-relaxed text-slate-500">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-500/70" />
                    This is a looping preview. The real assistant answers from live docs, the changelog, and your own app's data.
                </p>
                <div className="flex items-center gap-3">
                    <a
                        data-testid="assistant-signup-cta"
                        href="/signup"
                        className="rounded-md bg-cyan-400 px-4 py-2 text-xs font-bold text-slate-950 transition-colors hover:bg-cyan-300"
                    >
                        Sign up to try it
                    </a>
                    <a
                        data-testid="assistant-login-link"
                        href="/login"
                        className="font-mono text-[11px] text-slate-500 transition-colors hover:text-cyan-300"
                    >
                        Log in
                    </a>
                </div>
            </div>
        </div>
    );
}
